import React, { useEffect, useState } from "react";
import { NewsService, NewsArticle } from "../services/newsService";
import { PanelHeader, PanelState } from "./Panel";

export const BBCNews = () => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const newsService = new NewsService();
        const result = await newsService.getBBCNews();

        if (!result || !result.articles || result.articles.length === 0) {
          setError("No articles found");
          setArticles([]);
        } else {
          setArticles(result.articles.slice(0, 5)); // Show top 5 stories
          setError(null);
        }
      } catch (e: any) {
        let errorMsg = "Failed to load news";

        // Try to extract error message from response
        if (e?.message) {
          try {
            const parsed = JSON.parse(e.message);
            errorMsg = parsed.error || parsed.message || errorMsg;
          } catch {
            errorMsg = e.message;
          }
        }

        setError(errorMsg);
        console.error("Failed to fetch BBC News:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();

    // Refresh news every 30 minutes
    const interval = setInterval(fetchNews, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const time = (pubDate: string) => {
    const d = new Date(pubDate);
    return isNaN(d.getTime())
      ? "--:--"
      : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  let body: React.ReactNode;
  if (loading && articles.length === 0) {
    body = <PanelState>Loading…</PanelState>;
  } else if (error) {
    body = <PanelState tone="error">{error}</PanelState>;
  } else {
    body = (
      <div className="hud-list bbc-news__articles">
        {articles.map((article, index) => (
          <a
            key={article.link || index}
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            className={`bbc-news__article ${index === 0 ? "is-latest" : ""}`}
          >
            <span className="bbc-news__time">{time(article.pubDate)}</span>
            <span className="bbc-news__title">{article.title}</span>
            {index === 0 && <span className="bbc-news__tag">Latest</span>}
          </a>
        ))}
      </div>
    );
  }

  return (
    <div className="bbc-news">
      <PanelHeader
        label="News feed"
        meta={articles.length ? `BBC · ${articles.length} stories` : "BBC"}
      />
      {body}
    </div>
  );
};
